/** Signed-out screens: login, sign-up, 2FA (setup + code), email verification, password reset. */
import { Button, CopyButton, Field, Input, Notice, QrCode, buttonClasses } from "@custos/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { AuthLayout } from "../components/AuthLayout";
import { CodeInput } from "../components/bits";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { errorMessage, isApiError } from "../lib/errors";
import { useConfig } from "../lib/queries";

const MIN_PASSWORD = 12;

/** Reads "#token=…" from the URL once, then removes it from the address bar. */
function useHashParam(name: string): string | null {
  const [value] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get(name));
  useEffect(() => {
    if (window.location.hash)
      history.replaceState(null, "", window.location.pathname + window.location.search);
  }, []);
  return value;
}

function TextLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="font-medium text-primary-strong hover:underline">
      {children}
    </Link>
  );
}

// ───────────────────────── Login ─────────────────────────

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfig();
  const notice = useAuth((s) => s.notice);
  const setChallenge = useAuth((s) => s.setChallenge);
  const setNotice = useAuth((s) => s.setNotice);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const googleError = location.hash.includes("error=google_failed");

  const login = useMutation({
    mutationFn: () => api.login(email, password),
    onSuccess: (step) => {
      setNotice(null);
      setChallenge({ token: step.challengeToken, status: step.status, email });
      navigate("/login/2fa", { state: location.state });
    },
  });
  const resend = useMutation({ mutationFn: () => api.resendVerification(email) });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };

  return (
    <AuthLayout title="Log in" subtitle="Welcome back. You'll need your authenticator app.">
      <form onSubmit={submit} className="flex flex-col gap-4">
        {notice && <Notice tone="info">{notice}</Notice>}
        {googleError && <Notice tone="danger">Google sign-in didn't complete. Please try again.</Notice>}
        <Field label="Email">
          {(p) => (
            <Input
              {...p}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          )}
        </Field>
        <Field label="Password">
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          )}
        </Field>
        {login.error && (
          <Notice tone="danger">
            {errorMessage(login.error)}
            {isApiError(login.error, "EMAIL_NOT_VERIFIED") && (
              <div className="mt-2">
                {resend.isSuccess ? (
                  "A new verification link is on its way."
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={resend.isPending}
                    onClick={() => resend.mutate()}
                  >
                    Send a new link
                  </Button>
                )}
              </div>
            )}
          </Notice>
        )}
        <Button type="submit" block loading={login.isPending}>
          Continue
        </Button>
        {config.data?.googleAuthEnabled && (
          <a
            href={`${import.meta.env.VITE_API_URL || "/api"}/auth/google/start`}
            className={buttonClasses("secondary", "md", true)}
          >
            Continue with Google
          </a>
        )}
        <div className="flex flex-wrap justify-between gap-2 text-sm">
          <TextLink to="/forgot-password">Forgot password?</TextLink>
          <span className="text-muted">
            New here? <TextLink to="/register">Create an account</TextLink>
          </span>
        </div>
      </form>
    </AuthLayout>
  );
}

// ───────────────────────── 2FA step ─────────────────────────

export function TwoFactorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const challenge = useAuth((s) => s.challenge);
  const setChallenge = useAuth((s) => s.setChallenge);
  const [code, setCode] = useState("");
  const setupNeeded = challenge?.status === "totp_setup_required";

  // The secret is created once per login attempt; the query cache stops a second call from replacing it.
  const setup = useQuery({
    queryKey: ["totp-setup", challenge?.token],
    queryFn: () => api.totpSetup(challenge!.token),
    enabled: setupNeeded,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const verify = useMutation({
    mutationFn: () =>
      setupNeeded ? api.totpEnable(challenge!.token, code) : api.loginTotp(challenge!.token, code),
    onSuccess: () => {
      setChallenge(null);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from && from.startsWith("/") ? from : "/", { replace: true });
    },
  });

  if (!challenge) return <Navigate to="/login" replace />;
  const expired =
    isApiError(verify.error, "INVALID_CHALLENGE") || isApiError(setup.error, "INVALID_CHALLENGE");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (code.length === 6) verify.mutate();
  };

  return (
    <AuthLayout
      title={setupNeeded ? "Set up two-factor authentication" : "Enter your 2FA code"}
      subtitle={
        setupNeeded
          ? "Two-factor authentication is required for every login and withdrawal."
          : "Open your authenticator app and enter the 6-digit code."
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-5">
        {setupNeeded && (
          <ol className="flex flex-col gap-4 text-sm text-muted">
            <li>
              <span className="font-medium text-fg">1. Install an authenticator app</span> — Google
              Authenticator, Microsoft Authenticator, Authy or similar.
            </li>
            <li className="flex flex-col gap-3">
              <span>
                <span className="font-medium text-fg">2. Scan this QR code</span> with the app (or type the
                key below).
              </span>
              {setup.isPending && <div className="size-[208px] animate-pulse rounded-card bg-hover" />}
              {setup.data && (
                <>
                  <QrCode value={setup.data.otpauthUrl} label="2FA setup QR code" className="self-center" />
                  <div className="flex items-center gap-2 rounded-field border border-line bg-soft px-3 py-2">
                    <code
                      className="min-w-0 flex-1 font-mono text-xs break-all text-fg"
                      data-testid="totp-secret"
                    >
                      {setup.data.secret.replace(/(.{4})/g, "$1 ").trim()}
                    </code>
                    <CopyButton value={setup.data.secret} label="Copy key" />
                  </div>
                  <p className="text-xs">Keep this key private. Anyone with it can generate your codes.</p>
                </>
              )}
              {setup.error && !expired && <Notice tone="danger">{errorMessage(setup.error)}</Notice>}
            </li>
            <li className="font-medium text-fg">3. Enter the 6-digit code the app shows</li>
          </ol>
        )}
        <Field label="Authenticator code">
          {(p) => <CodeInput {...p} value={code} onChange={setCode} autoFocus />}
        </Field>
        {expired ? (
          <Notice tone="warning" title="This login step expired">
            <Link to="/login" className="text-primary-strong hover:underline">
              Log in again
            </Link>
          </Notice>
        ) : (
          verify.error && <Notice tone="danger">{errorMessage(verify.error)}</Notice>
        )}
        <Button
          type="submit"
          block
          loading={verify.isPending}
          disabled={code.length !== 6 || (setupNeeded && !setup.data)}
        >
          {setupNeeded ? "Turn on 2FA and continue" : "Verify"}
        </Button>
        <button
          type="button"
          className="text-sm text-muted hover:text-fg"
          onClick={() => {
            setChallenge(null);
            navigate("/login");
          }}
        >
          Use a different account
        </button>
      </form>
    </AuthLayout>
  );
}

// ───────────────────────── Sign-up ─────────────────────────

export function RegisterPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);
  const register = useMutation({ mutationFn: () => api.register(email, password) });
  const resend = useMutation({ mutationFn: () => api.resendVerification(email) });

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const mismatch = touched && confirm.length > 0 && confirm !== password;

  if (register.isSuccess) {
    return (
      <AuthLayout title="Check your email" subtitle={`We sent a verification link to ${email}.`}>
        <div className="flex flex-col gap-4">
          <Notice tone="info">
            Open the link to verify your address, then log in. During development the email is printed in the
            API window instead of being sent.
          </Notice>
          {resend.isSuccess ? (
            <Notice tone="success">A new link is on its way.</Notice>
          ) : (
            <Button variant="secondary" block loading={resend.isPending} onClick={() => resend.mutate()}>
              Send the link again
            </Button>
          )}
          <Link to="/login" className={buttonClasses("primary", "md", true)}>
            Go to login
          </Link>
        </div>
      </AuthLayout>
    );
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (password.length < MIN_PASSWORD || confirm !== password) return;
    register.mutate();
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Email, a strong password, then two-factor authentication."
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label="Email">
          {(p) => (
            <Input
              {...p}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          )}
        </Field>
        <Field
          label="Password"
          hint={`At least ${MIN_PASSWORD} characters. A short sentence is easy to remember and hard to guess.`}
          error={tooShort && touched ? `Use at least ${MIN_PASSWORD} characters.` : null}
        >
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          )}
        </Field>
        <Field label="Repeat password" error={mismatch ? "The passwords don't match." : null}>
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          )}
        </Field>
        {register.error && <Notice tone="danger">{errorMessage(register.error)}</Notice>}
        <Button type="submit" block loading={register.isPending}>
          Create account
        </Button>
        <p className="text-center text-sm text-muted">
          Already have an account? <TextLink to="/login">Log in</TextLink>
        </p>
      </form>
    </AuthLayout>
  );
}

// ───────────────────────── Email verification link ─────────────────────────

export function VerifyEmailPage() {
  const token = useHashParam("token");
  const started = useRef(false);
  const verify = useMutation({ mutationFn: (t: string) => api.verifyEmail(t) });

  useEffect(() => {
    if (token && !started.current) {
      started.current = true;
      verify.mutate(token);
    }
  }, [token, verify]);

  return (
    <AuthLayout title="Verify email">
      <div className="flex flex-col gap-4">
        {!token && <Notice tone="danger">This link is incomplete. Open the full link from the email.</Notice>}
        {verify.isPending && <p className="text-sm text-muted">Checking your link…</p>}
        {verify.isSuccess && (
          <Notice tone="success" title="Your email is verified">
            You can now log in and set up 2FA.
          </Notice>
        )}
        {verify.error && <Notice tone="danger">{errorMessage(verify.error)}</Notice>}
        <Link to="/login" className={buttonClasses("primary", "md", true)}>
          Go to login
        </Link>
      </div>
    </AuthLayout>
  );
}

// ───────────────────────── Password reset ─────────────────────────

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const forgot = useMutation({ mutationFn: () => api.forgotPassword(email) });
  return (
    <AuthLayout title="Reset password" subtitle="We'll email you a link to choose a new password.">
      {forgot.isSuccess ? (
        <div className="flex flex-col gap-4">
          <Notice tone="success">If an account exists for {email}, a reset link is on its way.</Notice>
          <Link to="/login" className={buttonClasses("secondary", "md", true)}>
            Back to login
          </Link>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            forgot.mutate();
          }}
          className="flex flex-col gap-4"
        >
          <Field label="Email">
            {(p) => (
              <Input
                {...p}
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            )}
          </Field>
          {forgot.error && <Notice tone="danger">{errorMessage(forgot.error)}</Notice>}
          <Button type="submit" block loading={forgot.isPending}>
            Send reset link
          </Button>
          <TextLink to="/login">Back to login</TextLink>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const token = useHashParam("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const reset = useMutation({ mutationFn: () => api.resetPassword(token!, password, code || undefined) });
  const valid = password.length >= MIN_PASSWORD && password === confirm;

  return (
    <AuthLayout title="Choose a new password" subtitle="For your security, this logs you out on all devices.">
      {reset.isSuccess ? (
        <div className="flex flex-col gap-4">
          <Notice tone="success">Password changed. Log in with your new password.</Notice>
          <Link to="/login" className={buttonClasses("primary", "md", true)}>
            Go to login
          </Link>
        </div>
      ) : !token ? (
        <Notice tone="danger">This link is incomplete. Open the full link from the email.</Notice>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) reset.mutate();
          }}
          className="flex flex-col gap-4"
        >
          <Field label="New password" hint={`At least ${MIN_PASSWORD} characters.`}>
            {(p) => (
              <Input
                {...p}
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Repeat new password"
            error={confirm && confirm !== password ? "The passwords don't match." : null}
          >
            {(p) => (
              <Input
                {...p}
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            )}
          </Field>
          <Field label="2FA code" hint="Needed if you already set up two-factor authentication.">
            {(p) => <CodeInput {...p} value={code} onChange={setCode} />}
          </Field>
          {reset.error && <Notice tone="danger">{errorMessage(reset.error)}</Notice>}
          <Button type="submit" block loading={reset.isPending} disabled={!valid}>
            Change password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

// ───────────────────────── Google sign-in return (only when enabled) ─────────────────────────

export function GoogleCompletePage() {
  const status = useHashParam("status");
  const challenge = useHashParam("challenge");
  const setChallenge = useAuth((s) => s.setChallenge);
  const navigate = useNavigate();
  useEffect(() => {
    if (challenge && (status === "totp_required" || status === "totp_setup_required")) {
      setChallenge({ token: challenge, status, email: null });
      navigate("/login/2fa", { replace: true });
    } else {
      navigate("/login#error=google_failed", { replace: true });
    }
  }, [challenge, status, setChallenge, navigate]);
  return null;
}
